import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ArcgisService } from '../../../pages/dashboard/services/arcgis/arcgis.service';
import type WebMap from '@arcgis/core/WebMap';
import type MapView from '@arcgis/core/views/MapView';
import { Company } from '../../interfaces/company';

@Component({
  selector: 'app-arcgis-webmap',
  standalone: true,
  imports: [],
  templateUrl: './arcgis-webmap.component.html',
  styleUrl: './arcgis-webmap.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ArcgisWebmapComponent implements OnInit, AfterViewInit, OnDestroy {
  arcgisService = inject(ArcgisService);
  private cdr = inject(ChangeDetectorRef);
  private readonly maxTokenRetryAttempts = 3;
  private tokenRetryAttempts = 0;
  private retryTimerId?: ReturnType<typeof setTimeout>;

  @Output() mapReady = new EventEmitter<void>();

  @ViewChild('mapView', { static: false })
  private mapViewEl!: ElementRef;

  @ViewChild('mapContainer', { static: true })
  private mapContainerEl!: ElementRef;

  baseMap: WebMap | undefined;
  mapView: MapView | undefined;
  isLayerListExpanded = true;
  isServiceDown = false;
  isLoading = true;
  private isMapInitializing = false;

  ngOnInit(): void {
    // Initialize with expanded state
    this.mapContainerEl.nativeElement.classList.add('layer-list-expanded');
  }

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  ngOnDestroy(): void {
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = undefined;
    }
    if (this.mapView) {
      this.mapView.destroy();
      this.mapView = undefined;
    }
    if (this.baseMap) {
      this.baseMap = undefined;
    }
  }

  showMapPoints(companies: Company[]): void {
    if (this.baseMap && this.mapView && this.mapViewEl) {
      this.arcgisService
        .AddPointsToWebMap(this.baseMap, this.mapView, companies)
        .then(({ webmap, view }) => {
          this.baseMap = webmap;
          this.mapView = view;
          if (this.mapContainerEl) {
            this.mapContainerEl.nativeElement.style.height = '700px';
          }
          if (this.mapViewEl) {
            this.mapViewEl.nativeElement.style.height = '500px';
          }
          this.mapView.openPopup({
            title: `<p style="font-size:15px;">Map Points Details</p>`,
            content: `<p style="font-size:13px;">Click on map points to show more details</p>`,
          });
        })
        .catch((err) => {
          throw err;
        });
    }
  }

  clearMapPoints(): void {
    if (this.baseMap) {
      const graphicsLayer = this.baseMap.layers.find(
        (layer) => layer.title === 'Company Markers Layer',
      );
      if (graphicsLayer && 'removeAll' in graphicsLayer) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (graphicsLayer as any).removeAll();
      }
      if (this.mapView) {
        this.mapView.closePopup();
      }
    }
  }

  toggleLayerList(): void {
    this.isLayerListExpanded = !this.isLayerListExpanded;
    const mapContainer = this.mapContainerEl.nativeElement;
    if (this.isLayerListExpanded) {
      mapContainer.classList.remove('layer-list-collapsed');
      mapContainer.classList.add('layer-list-expanded');
    } else {
      mapContainer.classList.remove('layer-list-expanded');
      mapContainer.classList.add('layer-list-collapsed');
    }
  }

  retryMapLoad(forceTokenRefresh = false): void {
    if (this.isMapInitializing) {
      return;
    }

    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = undefined;
    }

    if (forceTokenRefresh) {
      this.arcgisService.clearArcgisTokensFromStorage();
    }

    this.isServiceDown = false;
    this.isLoading = true;

    this.destroyMapResources();
    this.cdr.detectChanges();

    setTimeout(() => {
      this.initializeMap();
    }, 0);
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    this.retryIfServiceDown();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.hidden) {
      return;
    }
    this.retryIfServiceDown();
  }

  private retryIfServiceDown(): void {
    if (this.isServiceDown && !this.isMapInitializing) {
      this.retryMapLoad();
    }
  }

  private initializeMap(): void {
    if (this.isMapInitializing || !this.mapViewEl) {
      return;
    }

    this.isMapInitializing = true;
    this.isLoading = true;

    this.arcgisService
      .loadWebMap(this.mapViewEl.nativeElement)
      .then(({ webMap, view }) => {
        this.baseMap = webMap;
        this.mapView = view;
        this.isServiceDown = false;
        this.isLoading = false;
        this.tokenRetryAttempts = 0;
        this.mapReady.emit();
      })
      .catch((err) => {
        console.error('Failed to load ArcGIS web map:', err);
        if (this.shouldRetryForToken(err)) {
          const retryDelayMs = 500 * this.tokenRetryAttempts;
          this.retryTimerId = setTimeout(() => {
            this.retryMapLoad(true);
          }, retryDelayMs);
          return;
        }

        this.isServiceDown = true;
        this.isLoading = false;
      })
      .finally(() => {
        this.isMapInitializing = false;
      });
  }

  private shouldRetryForToken(err: unknown): boolean {
    const errorText = String(err).toLowerCase();
    const isTokenError =
      errorText.includes('token') ||
      errorText.includes('expired') ||
      errorText.includes('401') ||
      errorText.includes('498') ||
      errorText.includes('unauthorized');

    if (!isTokenError) {
      return false;
    }

    if (this.tokenRetryAttempts >= this.maxTokenRetryAttempts) {
      return false;
    }

    this.tokenRetryAttempts += 1;
    return true;
  }

  private destroyMapResources(): void {
    if (this.mapView) {
      this.mapView.destroy();
      this.mapView = undefined;
    }
    this.baseMap = undefined;
  }
}
